
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Eye, Edit, Trash2, Printer, CheckCircle, XCircle,
  CreditCard, Truck, Calendar, User, Search, Filter, ShoppingCart, Shield,
  ArrowRight, Download, PenTool, AlertTriangle, ChevronRight,
  MoreVertical, RefreshCw, X, Loader2, Send, Copy, ArrowRightLeft,
  ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import { Button, Input, Badge, Card, Modal, FieldHint, InfoTooltip, ConfirmationDialog, useToast } from './ui';
import { SignaturePad } from './SignaturePad';
import { QuoteStatus, InvoiceStatus, PaymentMethod } from '../types';
import type { QuoteItem } from '../types';
import {
  salesService,
  Quote as ApiQuote,
  Invoice as ApiInvoice,
  QuoteCreate,
  InvoiceCreate,
  Payment,
  PaymentCreate,
  InvoicePaymentSummary,
  DeliveryNote as ApiDeliveryNote,
  DeliveryNoteCreate,
  SaleType,
  clientsService,
  Client as ApiClient,
  stockService,
  Product as ApiProduct,
  Site,
} from '../src/services';
import { rentalService } from '../src/services/rentalService';
import { loaService } from '../src/services/loaService';
import apiClient from '../src/services/apiClient';
import { PaymentMethodSelect, PaymentDetails, EMPTY_PAYMENT_DETAILS, isPaymentValid } from './PaymentFields';
import { ENDPOINTS } from '../src/config/api';
import { useMutation } from '../src/hooks/useApi';

// Fonction "remise" suspendue : le champ est grisé côté saisie et le backend
// force les taux reçus à 0 (voir backend/app/core/remise.py). Passer à true
// pour la réactiver — les remises déjà enregistrées restent affichées.
const REMISE_ENABLED = false;

// Local types for component
interface Quote {
  id: string;
  uuid: string;
  typeVente: SaleType;
  clientId: string;
  clientName: string;
  date: string;
  validUntil: string;
  items: QuoteItem[];
  subtotal: number;
  tax: number;
  total: number;
  status: QuoteStatus;
}

interface Invoice {
  id: string;
  uuid: string;
  typeVente: SaleType;
  clientId: string;
  clientName: string;
  type: string;
  date: string;
  dueDate: string;
  items: QuoteItem[];
  subtotal: number;
  tax: number;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  /** Payments awaiting validation: reserved on the balance, not yet in paidAmount. */
  pendingAmount: number;
  status: InvoiceStatus;
  payments: InvoicePaymentSummary[];
  blGenere: boolean;
  dateDebutLocation?: string;
  dateFinLocation?: string;
  siteStockId?: string;
  siteStockNom?: string;
}

interface DeliveryNote {
  id: string;
  uuid: string;
  typeVente: SaleType;
  invoiceId: string;
  invoiceCode: string;
  clientName: string;
  date: string;
  address: string;
  status: string;
  driverName: string;
  items: { productId: string; productName: string; quantityOrdered: number; quantityDelivered: number }[];
  signatureClient?: string;
  signatureLivreur?: string;
  notes?: string;
}

interface Client {
  id: string;
  name: string;
  company?: string;
  isVatExempt: boolean;
  categorieClient: 'DETAILLANT' | 'GROSSISTE';
  address: string;
  city: string;
}

interface Product {
  id: string;
  name: string;
  priceSale: number;
  priceWholesale: number;
}

// Transform functions
const transformQuote = (q: ApiQuote): Quote => ({
  id: q.code || q.id,
  uuid: q.id,
  typeVente: q.type_vente || 'VENTE',
  clientId: q.client_id,
  clientName: q.client_nom || 'Client',
  date: q.date_devis,
  validUntil: q.date_validite,
  items: q.lignes?.map(l => ({
    productId: l.product_id,
    productName: l.product_nom || l.designation || '',
    quantity: l.quantite,
    unitPrice: l.prix_unitaire,
    discount: l.taux_remise || 0,
    total: l.montant_ht,
  })) || [],
  subtotal: Number(q.montant_ht) || 0,
  tax: Number(q.montant_tva) || 0,
  total: Number(q.montant_ttc) || 0,
  status: (q.status as QuoteStatus) || QuoteStatus.DRAFT,
});

const PAYMENT_LABELS: Record<string, string> = {
  ESPECES: 'Espèces', CHEQUE: 'Chèque', VIREMENT: 'Virement',
  MOBILE_MONEY_MTN: 'MTN MoMo', MOBILE_MONEY_MOOV: 'Moov Money',
};

const transformInvoice = (inv: ApiInvoice): Invoice => ({
  id: inv.code || inv.id,
  uuid: inv.id,
  typeVente: inv.type_vente || 'VENTE',
  clientId: inv.client_id,
  clientName: inv.client_nom || 'Client',
  type: inv.type_facture,
  date: inv.date_facture,
  dueDate: inv.date_echeance,
  items: inv.lignes?.map(l => ({
    productId: l.product_id,
    productName: l.product_nom || l.designation || '',
    quantity: l.quantite,
    unitPrice: l.prix_unitaire,
    discount: l.taux_remise || 0,
    total: l.montant_ht,
    factureLigneId: l.id,
    quantityDelivered: l.quantite_livree ?? 0,
    quantityRemaining: l.quantite_restante ?? l.quantite,
  })) || [],
  subtotal: Number(inv.montant_ht) || 0,
  tax: Number(inv.montant_tva) || 0,
  total: Number(inv.montant_ttc) || 0,
  paidAmount: inv.montant_paye || 0,
  remainingAmount: inv.montant_restant || inv.montant_ttc,
  pendingAmount: Number(inv.montant_en_attente ?? 0),
  status: (inv.status as InvoiceStatus) || InvoiceStatus.DRAFT,
  payments: inv.paiements || [],
  blGenere: false,
  dateDebutLocation: inv.date_debut_location || undefined,
  dateFinLocation: inv.date_fin_location || undefined,
  siteStockId: inv.site_stock_id || undefined,
  siteStockNom: inv.site_stock_nom || undefined,
});

const transformDeliveryNote = (dn: ApiDeliveryNote): DeliveryNote => ({
  id: dn.code || dn.id,
  uuid: dn.id,
  typeVente: dn.type_vente || 'VENTE',
  invoiceId: dn.facture_id,
  invoiceCode: dn.facture_code || '',
  clientName: dn.client_nom || 'Client',
  date: dn.date_livraison,
  address: dn.adresse_livraison || '',
  status: dn.status,
  driverName: dn.livreur_nom || '',
  items: dn.lignes?.map(l => ({
    productId: l.product_id,
    productName: l.product_nom || l.designation || l.product_reference || '',
    quantityOrdered: l.quantite_commandee,
    quantityDelivered: l.quantite_livree,
  })) || [],
  signatureClient: dn.signature_client,
  signatureLivreur: dn.signature_livreur,
  notes: dn.notes,
});

const transformClient = (c: ApiClient): Client => ({
  id: c.id,
  name: c.nom,
  company: c.raison_sociale,
  isVatExempt: c.is_exonere,
  categorieClient: c.categorie_client || 'DETAILLANT',
  address: c.adresse || '',
  city: c.ville || '',
});

const formatCurrency = (val: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(val);

// --- Badges ---
const QuoteStatusBadge = ({ status }: { status: QuoteStatus }) => {
  const styles: Record<string, string> = {
    [QuoteStatus.DRAFT]: 'gray',
    [QuoteStatus.SENT]: 'blue',
    [QuoteStatus.ACCEPTED]: 'green',
    [QuoteStatus.REJECTED]: 'red',
    [QuoteStatus.EXPIRED]: 'yellow',
    [QuoteStatus.CONVERTED]: 'purple',
    [QuoteStatus.CANCELLED]: 'gray',
  };
  const labels: Record<string, string> = {
    [QuoteStatus.DRAFT]: 'Brouillon',
    [QuoteStatus.SENT]: 'Envoyé',
    [QuoteStatus.ACCEPTED]: 'Accepté',
    [QuoteStatus.REJECTED]: 'Refusé',
    [QuoteStatus.EXPIRED]: 'Expiré',
    [QuoteStatus.CONVERTED]: 'Converti',
    [QuoteStatus.CANCELLED]: 'Annulé',
  };
  return <Badge color={(styles[status] || 'gray') as any}>{labels[status] || status}</Badge>;
};

const invoiceStatusLabels: Record<string, string> = {
  [InvoiceStatus.DRAFT]: 'Brouillon',
  [InvoiceStatus.VALIDATED]: 'Validée',
  [InvoiceStatus.PARTIAL]: 'Partielle',
  [InvoiceStatus.PAID]: 'Payée',
  [InvoiceStatus.OVERDUE]: 'En retard',
  [InvoiceStatus.CANCELLED]: 'Annulée',
};

const InvoiceStatusBadge = ({ status }: { status: InvoiceStatus }) => {
  const styles: Record<string, string> = {
    [InvoiceStatus.DRAFT]: 'gray',
    [InvoiceStatus.VALIDATED]: 'blue',
    [InvoiceStatus.PARTIAL]: 'yellow',
    [InvoiceStatus.PAID]: 'green',
    [InvoiceStatus.OVERDUE]: 'red',
    [InvoiceStatus.CANCELLED]: 'gray',
  };
  return <Badge color={(styles[status] || 'gray') as any}>{invoiceStatusLabels[status] || status}</Badge>;
};

const DeliveryNoteStatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    PENDING: 'yellow',
    IN_TRANSIT: 'blue',
    DELIVERED: 'green',
    PARTIAL: 'yellow',
    CANCELLED: 'red',
  };
  const labels: Record<string, string> = {
    PENDING: 'En attente',
    IN_TRANSIT: 'En transit',
    DELIVERED: 'Livré',
    PARTIAL: 'Partiel',
    CANCELLED: 'Annulé',
  };
  return <Badge color={(styles[status] || 'gray') as any}>{labels[status] || status}</Badge>;
};

const SaleTypeBadge = ({ type }: { type: SaleType }) => {
  const styles: Record<SaleType, string> = {
    VENTE: 'green',
    LOCATION: 'blue',
    LOA: 'purple',
  };
  const labels: Record<SaleType, string> = {
    VENTE: 'Vente',
    LOCATION: 'Location',
    LOA: 'LOA',
  };
  return <Badge color={(styles[type] || 'gray') as any}>{labels[type] || type}</Badge>;
};

// --- Wizard: Create Quote ---

interface CreateQuoteWizardProps {
  onClose: () => void;
  onSave: () => void;
  clients: Client[];
  products: Product[];
  userRole?: string;
}

const CreateQuoteWizard = ({ onClose, onSave, clients, products, userRole }: CreateQuoteWizardProps) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Quote> & {
    notes?: string;
    typeVente: SaleType;
    siteStockId?: string;
    dateDebutLocation?: string;
    dateFinPrevueLocation?: string;
    montantCaution?: number;
    tarifJournalier?: number;
    dureeMoisLoa?: number;
    tauxInteretAnnuelLoa?: number;
    apportInitialLoa?: number;
    valeurResiduelleLoa?: number;
  }>({
    clientId: '',
    typeVente: 'VENTE',
    siteStockId: '',
    date: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [],
    notes: '',
  });

  const [newItem, setNewItem] = useState<{productId: string, quantity: number, discount: number}>({
    productId: '', quantity: 1, discount: 0
  });
  const [validationError, setValidationError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [sites, setSites] = useState<Site[]>([]);
  const [clientResults, setClientResults] = useState<Client[]>(() => clients.slice(0, 50));
  const [isSearchingClients, setIsSearchingClients] = useState(false);
  const [pickedClient, setPickedClient] = useState<Client | null>(null);

  // Debounced server-side client search so the dropdown isn't capped by the
  // parent's initial page_size (we have more clients than the bootstrap list).
  useEffect(() => {
    const q = clientSearch.trim();
    if (q.length === 0) {
      setClientResults(clients.slice(0, 50));
      setIsSearchingClients(false);
      return;
    }
    setIsSearchingClients(true);
    const handle = setTimeout(async () => {
      try {
        const res = await clientsService.list({ search: q, page_size: 50, is_active: true });
        setClientResults(res.data.map(transformClient));
      } catch {
        setClientResults([]);
      } finally {
        setIsSearchingClients(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [clientSearch, clients]);

  useEffect(() => {
    let cancelled = false;
    stockService.listSites()
      .then(list => {
        if (cancelled) return;
        const actives = list.filter(s => s.is_active);
        setSites(actives);
        setFormData(prev => {
          if (prev.siteStockId) return prev;
          const def = actives.find(s => s.is_default) || actives[0];
          return def ? { ...prev, siteStockId: def.id } : prev;
        });
      })
      .catch(() => { if (!cancelled) setSites([]); });
    return () => { cancelled = true; };
  }, []);

  const { mutate, isLoading, error: mutationError } = useMutation(salesService.createQuote, {
    onSuccess: () => {
      onSave();
      onClose();
    },
  });

  const error = validationError || mutationError;

  const selectedClient =
    (formData.clientId
      ? (pickedClient && pickedClient.id === formData.clientId
          ? pickedClient
          : clients.find(c => c.id === formData.clientId)
            || clientResults.find(c => c.id === formData.clientId))
      : undefined);
  const filteredClients = clientResults.slice(0, 50);
  const selectedProduct = products.find(p => p.id === newItem.productId);

  const addItem = () => {
    if (!selectedProduct) return;
    const unitPrice = selectedClient?.categorieClient === 'GROSSISTE'
      ? selectedProduct.priceWholesale
      : selectedProduct.priceSale;
    const discount = REMISE_ENABLED ? newItem.discount : 0;
    const total = (unitPrice * newItem.quantity) * (1 - discount / 100);
    const item: QuoteItem = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      quantity: newItem.quantity,
      unitPrice: unitPrice,
      discount: discount,
      total: total
    };
    setFormData(prev => ({ ...prev, items: [...(prev.items || []), item] }));
    setNewItem({ productId: '', quantity: 1, discount: 0 });
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({ ...prev, items: prev.items?.filter((_, i) => i !== index) }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items?.reduce((sum, item) => sum + item.total, 0) || 0;
    const taxRate = selectedClient?.isVatExempt ? 0 : 0.18;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const { subtotal, tax, total } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
           <h3 className="text-xl font-bold text-slate-900">Nouveau Devis</h3>
           <button onClick={onClose}><X className="w-6 h-6 text-slate-500 hover:text-slate-700" /></button>
        </div>

        {/* Steps */}
        <div className="flex border-b border-slate-200">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 py-3 text-center text-sm font-medium border-b-2 ${step === s ? 'border-primary-500 text-primary-700' : 'border-transparent text-slate-400'}`}>
              Etape {s}: {s === 1 ? 'Client & Infos' : s === 2 ? 'Produits & Services' : 'Récapitulatif'}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4 max-w-lg mx-auto">
              {/* Type de vente */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Type de vente <span className="text-red-500 ml-0.5">*</span></label>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { value: 'VENTE' as SaleType, label: 'Vente directe', color: 'border-green-500 bg-green-50 text-green-700' },
                    { value: 'LOCATION' as SaleType, label: 'Location', color: 'border-blue-500 bg-blue-50 text-blue-700' },
                    { value: 'LOA' as SaleType, label: 'LOA / Crédit-bail', color: 'border-purple-500 bg-purple-50 text-purple-700' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({...formData, typeVente: opt.value})}
                      className={`p-3 rounded-lg border-2 text-sm font-medium text-center transition-all ${
                        formData.typeVente === opt.value ? opt.color : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client <span className="text-red-500 ml-0.5">*</span></label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-5 w-5 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    className="w-full border border-slate-300 rounded-md py-2 pl-9 pr-9 text-sm"
                    placeholder="Rechercher un client (nom, société, ville)..."
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                      if (formData.clientId) setFormData({...formData, clientId: ''});
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 150)}
                    disabled={isLoading}
                    autoComplete="off"
                  />
                  {(clientSearch || formData.clientId) && (
                    <button
                      type="button"
                      onClick={() => { setClientSearch(''); setFormData({...formData, clientId: ''}); setPickedClient(null); setShowClientDropdown(false); }}
                      className="absolute right-2 top-2 p-0.5 text-slate-400 hover:text-slate-600"
                      aria-label="Effacer"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                  {showClientDropdown && (
                    <div className="absolute z-20 mt-1 w-full max-h-60 overflow-auto bg-white border border-slate-200 rounded-md shadow-lg">
                      {isSearchingClients ? (
                        <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2">
                          <Loader2 className="w-3 h-3 animate-spin" /> Recherche en cours…
                        </div>
                      ) : filteredClients.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-slate-500">
                          {clientSearch.trim() ? 'Aucun client trouvé' : 'Tapez pour rechercher un client'}
                        </div>
                      ) : (
                        filteredClients.map(c => (
                          <button
                            type="button"
                            key={c.id}
                            onMouseDown={e => {
                              e.preventDefault();
                              setFormData({...formData, clientId: c.id});
                              setPickedClient(c);
                              setClientSearch(c.company || c.name);
                              setShowClientDropdown(false);
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-slate-100 last:border-0 ${formData.clientId === c.id ? 'bg-blue-50' : ''}`}
                          >
                            <div className="font-medium text-slate-800">{c.company || c.name}</div>
                            {c.company && c.name && c.company !== c.name && (
                              <div className="text-xs text-slate-500">{c.name}</div>
                            )}
                            {c.city && <div className="text-xs text-slate-400">{c.city}</div>}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <FieldHint>Le client détermine les conditions TVA et les tarifs applicables</FieldHint>
                {selectedClient && (
                  <div className="mt-2 p-3 bg-blue-50 text-blue-800 text-sm rounded border border-blue-100">
                    <p className="font-bold">{selectedClient.company || selectedClient.name}</p>
                    <p>{selectedClient.address}, {selectedClient.city}</p>
                    {selectedClient.isVatExempt && <Badge color="green" className="mt-1">Exonéré TVA</Badge>}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Date émission" type="date" required min={new Date().toISOString().split('T')[0]} value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} />
                <Input label="Validité jusqu'au" type="date" required hint="Le devis expire à cette date" min={formData.date || new Date().toISOString().split('T')[0]} value={formData.validUntil} onChange={e => setFormData({...formData, validUntil: e.target.value})} />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Site de livraison <span className="text-red-500 ml-0.5">*</span></label>
                <select
                  className="w-full border-slate-300 rounded-md text-sm py-2 px-3"
                  value={formData.siteStockId || ''}
                  onChange={e => setFormData({...formData, siteStockId: e.target.value})}
                  disabled={isLoading || sites.length === 0}
                  required
                >
                  <option value="">Choisir un site...</option>
                  {sites.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.nom}{s.ville ? ` — ${s.ville}` : ''}{s.is_default ? ' (par défaut)' : ''}
                    </option>
                  ))}
                </select>
                <FieldHint>Dépôt depuis lequel le client sera livré. Pré-remplit le bon de livraison ; modifiable au paiement.</FieldHint>
              </div>

              {/* LOCATION conditional fields */}
              {formData.typeVente === 'LOCATION' && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200 space-y-3">
                  <h4 className="text-sm font-semibold text-blue-800">Paramètres Location</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Date début" type="date" required min={new Date().toISOString().split('T')[0]} value={formData.dateDebutLocation || ''} onChange={e => setFormData({...formData, dateDebutLocation: e.target.value})} />
                    <Input label="Date fin prévue" type="date" required min={formData.dateDebutLocation || new Date().toISOString().split('T')[0]} value={formData.dateFinPrevueLocation || ''} onChange={e => setFormData({...formData, dateFinPrevueLocation: e.target.value})} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Tarif journalier (FCFA)" type="number" required value={formData.tarifJournalier ?? ''} onChange={e => setFormData({...formData, tarifJournalier: e.target.value !== '' ? parseFloat(e.target.value) : undefined})} />
                    <Input label="Montant caution (FCFA)" type="number" required value={formData.montantCaution ?? ''} onChange={e => setFormData({...formData, montantCaution: e.target.value !== '' ? parseFloat(e.target.value) : undefined})} />
                  </div>
                </div>
              )}

              {/* LOA conditional fields */}
              {formData.typeVente === 'LOA' && (
                <div className="p-4 rounded-lg bg-purple-50 border border-purple-200 space-y-3">
                  <h4 className="text-sm font-semibold text-purple-800">Paramètres LOA / Crédit-bail</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Durée (mois)" type="number" required value={formData.dureeMoisLoa ?? ''} onChange={e => setFormData({...formData, dureeMoisLoa: e.target.value !== '' ? parseInt(e.target.value) : undefined})} />
                    <Input label="Taux intérêt annuel (%)" type="number" required value={formData.tauxInteretAnnuelLoa ?? ''} onChange={e => setFormData({...formData, tauxInteretAnnuelLoa: e.target.value !== '' ? parseFloat(e.target.value) : undefined})} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input label="Apport initial (FCFA)" type="number" required value={formData.apportInitialLoa ?? ''} onChange={e => setFormData({...formData, apportInitialLoa: e.target.value !== '' ? parseFloat(e.target.value) : undefined})} />
                    <Input label="Valeur résiduelle (FCFA)" type="number" required value={formData.valeurResiduelleLoa ?? ''} onChange={e => setFormData({...formData, valeurResiduelleLoa: e.target.value !== '' ? parseFloat(e.target.value) : undefined})} />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes Internes / Conditions</label>
                <textarea
                  className="w-full border-slate-300 rounded-md p-2 text-sm"
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Conditions particulières..."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              {/* Add Item Form */}
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex gap-4 items-end">
                <div className="flex-1">
                   <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Produit <span className="text-red-500 ml-0.5">*</span></label>
                   <select
                     className="w-full border-slate-300 rounded-md text-sm py-1.5"
                     value={newItem.productId}
                     onChange={e => setNewItem({...newItem, productId: e.target.value})}
                     disabled={isLoading}
                   >
                     <option value="">Choisir...</option>
                     {products.map(p => {
                       const price = selectedClient?.categorieClient === 'GROSSISTE' ? p.priceWholesale : p.priceSale;
                       return <option key={p.id} value={p.id}>{p.name} ({formatCurrency(price)})</option>;
                     })}
                   </select>
                </div>
                <div className="w-24">
                   <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Qté <span className="text-red-500 ml-0.5">*</span></label>
                   <input
                     type="number" min="1"
                     className="w-full border-slate-300 rounded-md text-sm py-1.5"
                     value={newItem.quantity}
                     onChange={e => setNewItem({...newItem, quantity: parseInt(e.target.value)})}
                   />
                </div>
                <div className="w-24">
                   <label className={`block text-xs font-medium uppercase mb-1 ${REMISE_ENABLED ? 'text-slate-500' : 'text-slate-400'}`}>Remise %</label>
                   <input
                     type="number" min="0" max="100"
                     className={`w-full rounded-md text-sm py-1.5 ${REMISE_ENABLED ? 'border-slate-300' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                     value={REMISE_ENABLED ? newItem.discount : 0}
                     onChange={e => setNewItem({...newItem, discount: parseFloat(e.target.value)})}
                     disabled={!REMISE_ENABLED}
                     title={REMISE_ENABLED ? undefined : 'Fonction remise suspendue'}
                   />
                </div>
                <Button onClick={addItem} disabled={!newItem.productId}>Ajouter</Button>
              </div>

              {/* Items Table */}
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">PU</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Qté</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Remise</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Total HT</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                   {formData.items?.map((item, idx) => (
                     <tr key={idx}>
                       <td className="px-4 py-2 text-sm text-slate-900">{item.productName}</td>
                       <td className="px-4 py-2 text-sm text-right text-slate-600">{formatCurrency(item.unitPrice)}</td>
                       <td className="px-4 py-2 text-sm text-center text-slate-900">{item.quantity}</td>
                       <td className="px-4 py-2 text-sm text-center text-slate-600">{item.discount > 0 ? `-${item.discount}%` : '-'}</td>
                       <td className="px-4 py-2 text-sm text-right font-medium text-slate-900">{formatCurrency(item.total)}</td>
                       <td className="px-4 py-2 text-center">
                         <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                       </td>
                     </tr>
                   ))}
                   {formData.items?.length === 0 && (
                     <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Aucune ligne. Ajoutez des produits ci-dessus.</td></tr>
                   )}
                </tbody>
              </table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                   <div className="flex justify-between"><span>Sous-total HT:</span> <span className="font-medium">{formatCurrency(subtotal)}</span></div>
                   <div className="flex justify-between text-slate-600"><span>{tax === 0 ? 'TVA (Exonéré):' : 'TVA (18%):'}</span> <span>{formatCurrency(tax)}</span></div>
                   <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200"><span>Total TTC:</span> <span>{formatCurrency(total)}</span></div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 max-w-2xl mx-auto text-center">
               <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                 <CheckCircle className="w-8 h-8 text-green-600" />
               </div>
               <h3 className="text-2xl font-bold text-slate-900">Devis Prêt à Être Validé</h3>
               <p className="text-slate-500">Veuillez relire les informations avant la validation finale.</p>

               <div className="bg-slate-50 p-6 rounded-lg text-left border border-slate-200 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Client</span>
                    <span className="font-semibold">{selectedClient?.company || selectedClient?.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Montant TTC</span>
                    <span className="font-bold text-lg text-primary-700">{formatCurrency(total)}</span>
                  </div>
                  <div>
                     <span className="text-slate-500 block">Site de livraison</span>
                     <span className="font-semibold">{sites.find(s => s.id === formData.siteStockId)?.nom || '—'}</span>
                  </div>
                  <div>
                     <span className="text-slate-500 block">Échéance</span>
                     <span>{formData.validUntil}</span>
                  </div>
                  <div>
                     <span className="text-slate-500 block">Nombre d'articles</span>
                     <span>{formData.items?.length}</span>
                  </div>
               </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between">
          {error && (
            <div className="text-red-600 text-sm">{error}</div>
          )}
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep(s => s - 1)} disabled={isLoading}>
             {step === 1 ? 'Annuler' : 'Précédent'}
          </Button>
          <Button
            onClick={step === 3 ? async () => {
              const quoteData: QuoteCreate = {
                client_id: formData.clientId!,
                site_stock_id: formData.siteStockId!,
                type_vente: formData.typeVente,
                date_validite: formData.validUntil!,
                notes: formData.notes,
                lignes: formData.items?.map(item => ({
                  product_id: item.productId,
                  designation: item.productName,
                  quantite: item.quantity,
                  prix_unitaire: item.unitPrice,
                  taux_remise: item.discount,
                })) || [],
                // LOCATION fields
                ...(formData.typeVente === 'LOCATION' ? {
                  date_debut_location: formData.dateDebutLocation,
                  date_fin_prevue_location: formData.dateFinPrevueLocation,
                  montant_caution: formData.montantCaution,
                  tarif_journalier: formData.tarifJournalier,
                } : {}),
                // LOA fields
                ...(formData.typeVente === 'LOA' ? {
                  duree_mois_loa: formData.dureeMoisLoa,
                  taux_interet_annuel_loa: formData.tauxInteretAnnuelLoa,
                  apport_initial_loa: formData.apportInitialLoa,
                  valeur_residuelle_loa: formData.valeurResiduelleLoa,
                } : {}),
              };
              await mutate(quoteData);
            } : () => {
              if (step === 1) {
                setValidationError('');
                if (!formData.siteStockId) {
                  setValidationError('Veuillez choisir un site de livraison.');
                  return;
                }
                if (formData.date && formData.validUntil && formData.validUntil <= formData.date) {
                  setValidationError('La date de validité doit être postérieure à la date d\'émission');
                  return;
                }
                if (formData.typeVente === 'LOCATION') {
                  if (!formData.dateDebutLocation || !formData.dateFinPrevueLocation || !formData.tarifJournalier || !formData.montantCaution) {
                    setValidationError('Veuillez remplir tous les champs de location (dates, tarif journalier, caution).');
                    return;
                  }
                  if (formData.dateFinPrevueLocation <= formData.dateDebutLocation) {
                    setValidationError('La date de fin de location doit être postérieure à la date de début.');
                    return;
                  }
                }
                if (formData.typeVente === 'LOA') {
                  if (!formData.dureeMoisLoa || !formData.tauxInteretAnnuelLoa) {
                    setValidationError('Veuillez remplir tous les champs LOA (durée, taux d\'intérêt).');
                    return;
                  }
                }
              }
              setStep(s => s + 1);
            }}
            disabled={(step === 1 && (!formData.clientId || !formData.siteStockId)) || (step === 2 && (!formData.items || formData.items.length === 0)) || isLoading}
          >
             {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
             {step === 3 ? (isLoading ? 'Validation...' : 'Valider le Devis') : 'Suivant'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- Wizard: Create Invoice ---

interface CreateInvoiceWizardProps {
  onClose: () => void;
  onSave: () => void;
  clients: Client[];
  products: Product[];
  userRole?: string;
}

const CreateInvoiceWizard = ({ onClose, onSave, clients, products, userRole }: CreateInvoiceWizardProps) => {
  const isCommercial = userRole === 'COMMERCIAL';
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    clientId: '',
    type: 'PROFORMA' as 'PROFORMA' | 'DEFINITIVE' | 'AVOIR',
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    conditions: '',
    items: [] as QuoteItem[],
  });

  const [newItem, setNewItem] = useState<{productId: string, quantity: number, discount: number}>({
    productId: '', quantity: 1, discount: 0
  });

  const { mutate, isLoading, error } = useMutation(salesService.createInvoice, {
    onSuccess: () => {
      onSave();
      onClose();
    },
  });

  const selectedClient = clients.find(c => c.id === formData.clientId);
  const selectedProduct = products.find(p => p.id === newItem.productId);

  const addItem = () => {
    if (!selectedProduct) return;
    const unitPrice = selectedClient?.categorieClient === 'GROSSISTE'
      ? selectedProduct.priceWholesale
      : selectedProduct.priceSale;
    const discount = REMISE_ENABLED ? newItem.discount : 0;
    const total = (unitPrice * newItem.quantity) * (1 - discount / 100);
    const item: QuoteItem = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      quantity: newItem.quantity,
      unitPrice: unitPrice,
      discount: discount,
      total: total
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, item] }));
    setNewItem({ productId: '', quantity: 1, discount: 0 });
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.total, 0);
    const taxRate = selectedClient?.isVatExempt ? 0 : 0.18;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const { subtotal, tax, total } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
           <h3 className="text-xl font-bold text-slate-900">Nouvelle Facture</h3>
           <button onClick={onClose}><X className="w-6 h-6 text-slate-500 hover:text-slate-700" /></button>
        </div>

        <div className="flex border-b border-slate-200">
          {[1, 2, 3].map(s => (
            <div key={s} className={`flex-1 py-3 text-center text-sm font-medium border-b-2 ${step === s ? 'border-primary-500 text-primary-700' : 'border-transparent text-slate-400'}`}>
              Etape {s}: {s === 1 ? 'Client & Type' : s === 2 ? 'Produits & Services' : 'Récapitulatif'}
            </div>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {step === 1 && (
            <div className="space-y-4 max-w-lg mx-auto">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client <span className="text-red-500 ml-0.5">*</span></label>
                <select
                  className="w-full border-slate-300 rounded-md py-2"
                  value={formData.clientId}
                  onChange={e => setFormData({...formData, clientId: e.target.value})}
                  disabled={isLoading}
                >
                  <option value="">Sélectionner un client...</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                </select>
                {selectedClient && (
                  <div className="mt-2 p-3 bg-blue-50 text-blue-800 text-sm rounded border border-blue-100">
                    <p className="font-bold">{selectedClient.company || selectedClient.name}</p>
                    <p>{selectedClient.address}, {selectedClient.city}</p>
                    {selectedClient.isVatExempt && <Badge color="green" className="mt-1">Exonéré TVA</Badge>}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type de Facture <span className="text-red-500 ml-0.5">*</span></label>
                <select
                  className="w-full border-slate-300 rounded-md py-2"
                  value={formData.type}
                  onChange={e => setFormData({...formData, type: e.target.value as any})}
                >
                  <option value="PROFORMA">Proforma</option>
                  {!isCommercial && <option value="DEFINITIVE">Définitive</option>}
                  <option value="AVOIR">Avoir</option>
                </select>
              </div>
              <Input label="Date d'échéance" type="date" required min={new Date().toISOString().split('T')[0]} value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} />
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  className="w-full border-slate-300 rounded-md p-2 text-sm"
                  rows={3}
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Notes ou conditions..."
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex gap-4 items-end">
                <div className="flex-1">
                   <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Produit <span className="text-red-500 ml-0.5">*</span></label>
                   <select
                     className="w-full border-slate-300 rounded-md text-sm py-1.5"
                     value={newItem.productId}
                     onChange={e => setNewItem({...newItem, productId: e.target.value})}
                   >
                     <option value="">Choisir...</option>
                     {products.map(p => {
                       const price = selectedClient?.categorieClient === 'GROSSISTE' ? p.priceWholesale : p.priceSale;
                       return <option key={p.id} value={p.id}>{p.name} ({formatCurrency(price)})</option>;
                     })}
                   </select>
                </div>
                <div className="w-24">
                   <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Qté <span className="text-red-500 ml-0.5">*</span></label>
                   <input type="number" min="1" className="w-full border-slate-300 rounded-md text-sm py-1.5" value={newItem.quantity} onChange={e => setNewItem({...newItem, quantity: parseInt(e.target.value)})} />
                </div>
                <div className="w-24">
                   <label className={`block text-xs font-medium uppercase mb-1 ${REMISE_ENABLED ? 'text-slate-500' : 'text-slate-400'}`}>Remise %</label>
                   <input
                     type="number" min="0" max="100"
                     className={`w-full rounded-md text-sm py-1.5 ${REMISE_ENABLED ? 'border-slate-300' : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'}`}
                     value={REMISE_ENABLED ? newItem.discount : 0}
                     onChange={e => setNewItem({...newItem, discount: parseFloat(e.target.value)})}
                     disabled={!REMISE_ENABLED}
                     title={REMISE_ENABLED ? undefined : 'Fonction remise suspendue'}
                   />
                </div>
                <Button onClick={addItem} disabled={!newItem.productId}>Ajouter</Button>
              </div>

              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-slate-500 uppercase">Produit</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">PU</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Qté</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-slate-500 uppercase">Remise</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-slate-500 uppercase">Total HT</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                   {formData.items.map((item, idx) => (
                     <tr key={idx}>
                       <td className="px-4 py-2 text-sm text-slate-900">{item.productName}</td>
                       <td className="px-4 py-2 text-sm text-right text-slate-600">{formatCurrency(item.unitPrice)}</td>
                       <td className="px-4 py-2 text-sm text-center text-slate-900">{item.quantity}</td>
                       <td className="px-4 py-2 text-sm text-center text-slate-600">{item.discount > 0 ? `-${item.discount}%` : '-'}</td>
                       <td className="px-4 py-2 text-sm text-right font-medium text-slate-900">{formatCurrency(item.total)}</td>
                       <td className="px-4 py-2 text-center">
                         <button onClick={() => removeItem(idx)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                       </td>
                     </tr>
                   ))}
                   {formData.items.length === 0 && (
                     <tr><td colSpan={6} className="text-center py-8 text-slate-400 italic">Aucune ligne. Ajoutez des produits ci-dessus.</td></tr>
                   )}
                </tbody>
              </table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                   <div className="flex justify-between"><span>Sous-total HT:</span> <span className="font-medium">{formatCurrency(subtotal)}</span></div>
                   <div className="flex justify-between text-slate-600"><span>{tax === 0 ? 'TVA (Exonéré):' : 'TVA (18%):'}</span> <span>{formatCurrency(tax)}</span></div>
                   <div className="flex justify-between text-lg font-bold text-slate-900 pt-2 border-t border-slate-200"><span>Total TTC:</span> <span>{formatCurrency(total)}</span></div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6 max-w-2xl mx-auto text-center">
               <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                 <CheckCircle className="w-8 h-8 text-green-600" />
               </div>
               <h3 className="text-2xl font-bold text-slate-900">Facture Prête</h3>
               <p className="text-slate-500">Vérifiez les informations avant validation.</p>

               <div className="bg-slate-50 p-6 rounded-lg text-left border border-slate-200 grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500 block">Client</span>
                    <span className="font-semibold">{selectedClient?.company || selectedClient?.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">Montant TTC</span>
                    <span className="font-bold text-lg text-primary-700">{formatCurrency(total)}</span>
                  </div>
                  <div>
                     <span className="text-slate-500 block">Type</span>
                     <span>{formData.type}</span>
                  </div>
                  <div>
                     <span className="text-slate-500 block">Échéance</span>
                     <span>{formData.dueDate}</span>
                  </div>
               </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between">
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <Button variant="ghost" onClick={step === 1 ? onClose : () => setStep(s => s - 1)} disabled={isLoading}>
             {step === 1 ? 'Annuler' : 'Précédent'}
          </Button>
          <Button
            onClick={step === 3 ? async () => {
              const invoiceData: InvoiceCreate = {
                client_id: formData.clientId,
                type_facture: formData.type,
                date_echeance: formData.dueDate,
                notes: formData.notes || undefined,
                mentions_legales: formData.conditions || undefined,
                is_exonere_tva: selectedClient?.isVatExempt || false,
                lignes: formData.items.map(item => ({
                  product_id: item.productId,
                  designation: item.productName,
                  quantite: item.quantity,
                  prix_unitaire: item.unitPrice,
                  taux_remise: item.discount,
                })),
              };
              await mutate(invoiceData);
            } : () => setStep(s => s + 1)}
            disabled={(step === 1 && !formData.clientId) || (step === 2 && formData.items.length === 0) || isLoading}
          >
             {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
             {step === 3 ? (isLoading ? 'Validation...' : 'Créer la Facture') : 'Suivant'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- Create Delivery Note Modal ---

interface CreateDeliveryNoteModalProps {
  invoice: Invoice;
  onClose: () => void;
  onSave: () => void;
}

const CreateDeliveryNoteModal = ({ invoice, onClose, onSave }: CreateDeliveryNoteModalProps) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [siteId, setSiteId] = useState('');
  const [dateLivraison, setDateLivraison] = useState(new Date().toISOString().split('T')[0]);
  const [adresse, setAdresse] = useState('');
  const [livreurNom, setLivreurNom] = useState('');
  const [notes, setNotes] = useState('');
  // Only lines with a remaining quantity are deliverable. Default "Qté Livrée"
  // to the reste à livrer (ordered − already delivered) so partial BLs are
  // handled naturally and the backend over-delivery guard is never hit.
  const [lignes, setLignes] = useState(
    invoice.items
      .map(item => {
        const restant = item.quantityRemaining ?? item.quantity;
        return {
          facture_ligne_id: item.factureLigneId,
          product_id: item.productId,
          designation: item.productName,
          quantite_commandee: item.quantity,
          quantite_deja_livree: item.quantityDelivered ?? 0,
          quantite_restante: restant,
          quantite_livree: restant,
        };
      })
      .filter(l => l.quantite_restante > 0)
  );

  useEffect(() => {
    stockService.listSites().then(setSites).catch(() => {
      console.error('Failed to load stock sites');
      setSites([]);
    });
  }, []);

  const { mutate, isLoading, error } = useMutation(salesService.createDeliveryNote, {
    onSuccess: () => {
      onSave();
      onClose();
    },
  });

  const handleSubmit = async () => {
    const data: DeliveryNoteCreate = {
      facture_id: invoice.uuid,
      date_livraison: dateLivraison,
      adresse_livraison: adresse || undefined,
      livreur_nom: livreurNom || undefined,
      notes: notes || undefined,
      site_stock_id: siteId,
      lignes: lignes.map(l => ({
        facture_ligne_id: l.facture_ligne_id,
        product_id: l.product_id,
        designation: l.designation,
        quantite_commandee: l.quantite_commandee,
        quantite_livree: l.quantite_livree,
      })),
    };
    await mutate(data);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
           <h3 className="text-xl font-bold text-slate-900">Créer un Bon de Livraison</h3>
           <button onClick={onClose}><X className="w-6 h-6 text-slate-500 hover:text-slate-700" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="bg-blue-50 p-3 rounded border border-blue-100 text-sm text-blue-800">
            Facture : <span className="font-bold">{invoice.id}</span> - {invoice.clientName}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input label="Date de livraison" type="date" required min={new Date().toISOString().split('T')[0]} value={dateLivraison} onChange={e => setDateLivraison(e.target.value)} />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Site de stock <span className="text-red-500 ml-0.5">*</span></label>
              <select className="w-full border-slate-300 rounded-md py-2 text-sm" value={siteId} onChange={e => setSiteId(e.target.value)}>
                <option value="">Sélectionner un site...</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.nom} {s.ville ? `(${s.ville})` : ''}</option>)}
              </select>
            </div>
          </div>

          <Input label="Adresse de livraison" value={adresse} onChange={e => setAdresse(e.target.value)} placeholder="Adresse de livraison..." />
          <Input label="Nom du livreur" value={livreurNom} onChange={e => setLivreurNom(e.target.value)} placeholder="Nom du livreur..." />

          <div>
            <h4 className="font-bold text-slate-900 mb-2">Lignes de livraison</h4>
            {lignes.length === 0 ? (
              <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 p-3 rounded">
                Toutes les quantités de cette facture ont déjà été livrées.
              </div>
            ) : (
            <table className="min-w-full divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Produit</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté Commandée</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Déjà Livrée</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Reste à Livrer</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté Livrée</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lignes.map((l, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-slate-900">{l.designation}</td>
                    <td className="px-4 py-2 text-sm text-center text-slate-600">{l.quantite_commandee}</td>
                    <td className="px-4 py-2 text-sm text-center text-slate-600">{l.quantite_deja_livree}</td>
                    <td className="px-4 py-2 text-sm text-center font-medium text-slate-700">{l.quantite_restante}</td>
                    <td className="px-4 py-2 text-center">
                      <input
                        type="number" min="0" max={l.quantite_restante}
                        className="w-20 border-slate-300 rounded-md text-sm py-1 text-center"
                        value={l.quantite_livree}
                        onChange={e => {
                          const raw = parseInt(e.target.value) || 0;
                          const updated = [...lignes];
                          updated[idx] = { ...updated[idx], quantite_livree: Math.min(Math.max(raw, 0), l.quantite_restante) };
                          setLignes(updated);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea className="w-full border-slate-300 rounded-md p-2 text-sm" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes..." />
          </div>

          {error && <div className="text-red-600 text-sm bg-red-50 p-3 rounded">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!siteId || isLoading || lignes.length === 0 || lignes.every(l => l.quantite_livree <= 0)} icon={Truck}>
            {isLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Création...</> : 'Créer le BL'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// --- Reject Quote Modal ---

interface RejectQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}

const RejectQuoteModal = ({ isOpen, onClose, onConfirm, isLoading }: RejectQuoteModalProps) => {
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rejeter le Devis">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Veuillez indiquer le motif du rejet.</p>
        <textarea
          className="w-full border-slate-300 rounded-md p-2 text-sm"
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Motif du rejet..."
        />
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Annuler</Button>
          <Button variant="danger" onClick={() => onConfirm(reason)} disabled={!reason.trim() || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Rejeter
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// --- Cancel Invoice Modal ---

interface CancelInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  isLoading: boolean;
}

const CancelInvoiceModal = ({ isOpen, onClose, onConfirm, isLoading }: CancelInvoiceModalProps) => {
  const [reason, setReason] = useState('');
  if (!isOpen) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Annuler la Facture">
      <div className="space-y-4">
        <p className="text-sm text-slate-600">Veuillez indiquer le motif de l'annulation.</p>
        <textarea
          className="w-full border-slate-300 rounded-md p-2 text-sm"
          rows={3}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Motif de l'annulation..."
        />
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Annuler</Button>
          <Button variant="danger" onClick={() => onConfirm(reason)} disabled={!reason.trim() || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Confirmer l'annulation
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// --- Add Payment Modal ---

interface AddPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
  onAddPayment: (payment: PaymentCreate) => void;
  isLoading?: boolean;
}

const AddPaymentModal = ({ isOpen, onClose, invoice, onAddPayment, isLoading }: AddPaymentModalProps) => {
  // Payments awaiting validation already reserve part of the balance server-side,
  // even though montant_restant still shows them as unpaid. Saisir plus que ce
  // reliquat est refusé par le backend (PAYMENT_PENDING_VALIDATION).
  const pendingAmount = invoice.pendingAmount || 0;
  const enterableAmount = Math.max(invoice.remainingAmount - pendingAmount, 0);

  const [data, setData] = useState({
    amount: enterableAmount,
    date: new Date().toISOString().split('T')[0]
  });
  const [payment, setPayment] = useState<PaymentDetails>({ ...EMPTY_PAYMENT_DETAILS });
  const [sites, setSites] = useState<{ id: string; nom: string; ville?: string }[]>([]);
  const [siteId, setSiteId] = useState('');

  const willFullySettle = invoice.typeVente === 'VENTE' && data.amount >= enterableAmount && enterableAmount > 0;

  // Reset amount and fields when the modal opens or the invoice changes
  useEffect(() => {
    if (isOpen) {
      setData({
        amount: enterableAmount,
        date: new Date().toISOString().split('T')[0],
      });
      setPayment({ ...EMPTY_PAYMENT_DETAILS });
      setSiteId(invoice.siteStockId || '');
    }
  }, [isOpen, invoice.uuid, enterableAmount, invoice.siteStockId]);

  useEffect(() => {
    if (isOpen && invoice.typeVente === 'VENTE') {
      stockService.listSites().then(setSites).catch(() => setSites([]));
    }
  }, [isOpen, invoice.typeVente]);

  const handleSubmit = () => {
    onAddPayment({
      facture_id: invoice.uuid,
      montant: data.amount,
      date_paiement: data.date,
      mode_paiement: payment.mode_paiement as PaymentMethod,
      reference: payment.reference || undefined,
      banque: payment.banque || undefined,
      numero_telephone: payment.numero_telephone || undefined,
      transaction_id: payment.transaction_id || undefined,
      site_stock_id: willFullySettle && siteId ? siteId : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Encaisser Paiement - ${invoice.id}`}>
      <div className="space-y-4">
        <div className="bg-slate-100 p-3 rounded text-sm space-y-1">
           <div className="flex justify-between">
             <span>Reste à payer :</span>
             <span className="font-bold">{formatCurrency(invoice.remainingAmount)}</span>
           </div>
           {pendingAmount > 0 && (
             <>
               <div className="flex justify-between text-amber-700">
                 <span>Dont en attente de validation :</span>
                 <span className="font-bold">- {formatCurrency(pendingAmount)}</span>
               </div>
               <div className="flex justify-between border-t border-slate-300 pt-1">
                 <span>Montant encore saisissable :</span>
                 <span className="font-bold">{formatCurrency(enterableAmount)}</span>
               </div>
             </>
           )}
        </div>

        {pendingAmount > 0 && (
          <div className="p-3 rounded border border-amber-300 bg-amber-50 text-sm text-amber-900 flex gap-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 text-amber-600" />
            <span>
              {formatCurrency(pendingAmount)} de paiement(s) sont déjà enregistrés sur cette facture
              mais <strong>en attente de validation</strong>. Validez-les depuis l'historique des
              paiements plutôt que de les ressaisir — sinon l'enregistrement sera refusé.
            </span>
          </div>
        )}

        <Input
          label="Montant (FCFA)" type="number" required max={enterableAmount}
          hint={pendingAmount > 0
            ? 'Ne peut pas dépasser le montant encore saisissable (reste dû moins les paiements en attente)'
            : 'Ne peut pas dépasser le montant restant dû'}
          value={data.amount} onChange={e => setData({...data, amount: parseFloat(e.target.value)})}
        />

        <PaymentMethodSelect value={payment} onChange={setPayment} />

        {willFullySettle && (
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Dépôt d'enlèvement (BL auto-généré)
              <InfoTooltip text="Le paiement est toujours enregistré (prévente autorisée). Si le stock est disponible à ce dépôt, le BL est créé automatiquement; sinon il est différé et devra être créé manuellement après réception." />
            </label>
            <select
              className="w-full border-slate-300 rounded-md py-2 text-sm"
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
            >
              <option value="">{invoice.siteStockNom ? `Site du devis (${invoice.siteStockNom})` : 'Site par défaut'}</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.nom}{s.ville ? ` (${s.ville})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">
              {invoice.siteStockNom
                ? `Pré-rempli sur le site choisi dans le devis (${invoice.siteStockNom}). Vous pouvez le modifier ici.`
                : 'Aucun site défini sur la facture; le site par défaut sera utilisé.'}
              {' '}Prévente : le paiement est toujours enregistré. Le BL n'est créé que si le stock est suffisant au dépôt choisi; sinon il devra être créé manuellement après réception.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={!data.amount || data.amount <= 0 || data.amount > enterableAmount || !isPaymentValid(payment) || isLoading}>
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Enregistrer
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// --- Invoice Detail ---

interface InvoiceDetailProps {
  invoice: Invoice;
  onClose: () => void;
  onAddPayment: () => void;
  onRefresh: () => void;
  onCreateDeliveryNote: () => void;
  userRole?: string;
}

const InvoiceDetail = ({ invoice, onClose, onAddPayment, onRefresh, onCreateDeliveryNote, userRole }: InvoiceDetailProps) => {
  const isCommercial = userRole === 'COMMERCIAL';
  // POST /payments/{id}/validate is guarded by ComptableOrAdmin — mirror it here
  // so the button never shows to someone who would just get a 403.
  const canValidatePayment = userRole === 'ADMIN' || userRole === 'COMPTABLE';
  const [actionLoading, setActionLoading] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const { toast } = useToast();
  const isProforma = invoice.type === 'PROFORMA';

  const handleConvertToDefinitive = async () => {
    setActionLoading('convert');
    try {
      await salesService.convertProformaToDefinitive(invoice.uuid);
      toast('Facture convertie en définitive avec succès', 'success');
      onRefresh();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la conversion', 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleValidate = async () => {
    setActionLoading('validate');
    try {
      await salesService.validateInvoice(invoice.uuid);
      toast('Facture validée avec succès', 'success');
      onRefresh();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la validation', 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleValidatePayment = async (paymentId: string) => {
    setActionLoading(`pay-${paymentId}`);
    try {
      await salesService.validatePayment(paymentId);
      toast('Paiement validé', 'success');
      onRefresh();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la validation du paiement', 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handleCancel = async (reason: string) => {
    setActionLoading('cancel');
    try {
      await salesService.cancelInvoice(invoice.uuid, reason);
      toast('Facture annulée', 'success');
      setCancelModalOpen(false);
      onRefresh();
    } catch (err: any) {
      toast(err.message || "Erreur lors de l'annulation", 'error');
    } finally {
      setActionLoading('');
    }
  };

  const handlePdf = async () => {
    try {
      const blob = await salesService.getInvoicePdf(invoice.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${invoice.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.message || 'Erreur lors du téléchargement du PDF', 'error');
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[800px] bg-white shadow-2xl z-40 flex flex-col border-l border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-start bg-slate-50">
         <div>
            <div className="flex items-center gap-3">
               <h2 className="text-2xl font-bold text-slate-900">{invoice.id}</h2>
               <InvoiceStatusBadge status={invoice.status} />
            </div>
            <p className="text-slate-500">{invoice.clientName} {invoice.type && <span className="text-xs ml-2 bg-slate-200 rounded px-1.5 py-0.5">{invoice.type}</span>}</p>
         </div>
         <button onClick={onClose}><X className="w-6 h-6 text-slate-400 hover:text-slate-600" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-8">
         {/* Key Metrics */}
         <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
               <span className="text-xs text-slate-500 uppercase">Total TTC</span>
               <div className="text-xl font-bold text-slate-900">{formatCurrency(invoice.total)}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-100">
               <span className="text-xs text-green-700 uppercase">Payé</span>
               <div className="text-xl font-bold text-green-800">{formatCurrency(invoice.paidAmount)}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
               <span className="text-xs text-red-700 uppercase">Reste à payer</span>
               <div className="text-xl font-bold text-red-800">{formatCurrency(invoice.remainingAmount)}</div>
            </div>
         </div>

         {/* Payments recorded but not yet validated: they already block new entries */}
         {invoice.pendingAmount > 0 && (
           <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 flex items-start gap-3">
             <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
             <div className="text-sm text-amber-900">
               <span className="font-bold">{formatCurrency(invoice.pendingAmount)} en attente de validation.</span>{' '}
               Ce montant n'est pas encore compté dans « Payé », mais il est déjà réservé :
               le montant encore saisissable n'est que de{' '}
               <strong>{formatCurrency(Math.max(invoice.remainingAmount - invoice.pendingAmount, 0))}</strong>.
               Validez le(s) paiement(s) ci-dessous au lieu de les ressaisir.
             </div>
           </div>
         )}

         {/* Location Period */}
         {(invoice.typeVente === 'LOCATION' || invoice.typeVente === 'LOA') && invoice.dateDebutLocation && (
           <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-4">
             <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0" />
             <div>
               <span className="text-xs text-blue-700 uppercase font-medium">Période de {invoice.typeVente === 'LOCATION' ? 'location' : 'LOA'}</span>
               <div className="text-sm font-bold text-blue-900">
                 {new Date(invoice.dateDebutLocation).toLocaleDateString('fr-FR')} — {invoice.dateFinLocation ? new Date(invoice.dateFinLocation).toLocaleDateString('fr-FR') : '...'}
               </div>
             </div>
           </div>
         )}

         {/* Actions */}
         <div className="flex flex-wrap gap-3">
            {isProforma ? (
              <Button icon={ArrowRightLeft} onClick={handleConvertToDefinitive} disabled={actionLoading === 'convert'}>
                {actionLoading === 'convert' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Convertir en Définitive
              </Button>
            ) : !isCommercial ? (
              <Button onClick={onAddPayment} disabled={invoice.status === InvoiceStatus.PAID || invoice.status === InvoiceStatus.CANCELLED} icon={CreditCard}>Ajouter Paiement</Button>
            ) : null}
            <Button variant="secondary" icon={Printer} onClick={handlePdf}>Imprimer PDF</Button>
            {invoice.status === InvoiceStatus.DRAFT && (
              <Button variant="secondary" icon={CheckCircle} onClick={handleValidate} disabled={actionLoading === 'validate'}>
                {actionLoading === 'validate' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Valider la Facture
              </Button>
            )}
            {[InvoiceStatus.VALIDATED, InvoiceStatus.PARTIAL, InvoiceStatus.PAID].includes(invoice.status) && !isProforma && !isCommercial && (
              <Button variant="secondary" icon={Truck} onClick={onCreateDeliveryNote}>Créer Bon de Livraison</Button>
            )}
            {invoice.status !== InvoiceStatus.CANCELLED && invoice.status !== InvoiceStatus.PAID && !isCommercial && (
              <Button variant="danger" icon={XCircle} onClick={() => setCancelModalOpen(true)}>Annuler</Button>
            )}
         </div>

         {/* Line Items */}
         <div>
            <h3 className="font-bold text-slate-900 mb-3">Détail des lignes</h3>
            <table className="min-w-full divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
               <thead className="bg-slate-50">
                  <tr>
                     <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Produit</th>
                     <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté</th>
                     <th className="px-4 py-2 text-right text-xs font-medium text-slate-500">Total</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                  {invoice.items.map((item, i) => (
                     <tr key={i}>
                        <td className="px-4 py-2 text-sm">{item.productName}</td>
                        <td className="px-4 py-2 text-center text-sm">{item.quantity}</td>
                        <td className="px-4 py-2 text-right text-sm font-medium">{formatCurrency(item.total)}</td>
                     </tr>
                  ))}
               </tbody>
            </table>
            <div className="mt-3 text-right space-y-1">
              <div className="text-sm text-slate-600">Sous-total HT : <span className="font-medium">{formatCurrency(invoice.subtotal)}</span></div>
              <div className="text-sm text-slate-600">TVA : <span className="font-medium">{formatCurrency(invoice.tax)}</span></div>
              <div className="text-base font-bold text-slate-900">Total TTC : {formatCurrency(invoice.total)}</div>
            </div>
         </div>

         {/* Payment History */}
         <div>
            <h3 className="font-bold text-slate-900 mb-3">Historique des Paiements</h3>
            {invoice.payments.length > 0 ? (
               <div className="space-y-2">
                  {invoice.payments.map(p => {
                     const isPending = p.status === 'PENDING';
                     const isRejected = p.status === 'REJECTED';
                     return (
                       <div
                         key={p.id}
                         className={`flex justify-between items-center p-3 border rounded ${
                           isPending ? 'border-amber-300 bg-amber-50'
                             : isRejected ? 'border-slate-200 bg-slate-50 opacity-60'
                             : 'border-slate-200 bg-slate-50'
                         }`}
                       >
                          <div>
                             <div className="font-medium text-slate-900">
                               {formatCurrency(p.montant)}
                               <span className="ml-2 text-xs font-normal text-slate-500">{p.code}</span>
                             </div>
                             <div className="text-xs text-slate-500">
                               {p.date_paiement} - {PAYMENT_LABELS[p.mode_paiement] || p.mode_paiement}
                               {p.reference ? ` - Réf: ${p.reference}` : ''}
                             </div>
                          </div>
                          <div className="flex items-center gap-2">
                             {isPending ? (
                               <>
                                 <Badge color="yellow">En attente de validation</Badge>
                                 {canValidatePayment && (
                                   <Button
                                     variant="secondary"
                                     size="sm"
                                     onClick={() => handleValidatePayment(p.id)}
                                     disabled={actionLoading === `pay-${p.id}`}
                                   >
                                     {actionLoading === `pay-${p.id}`
                                       ? <Loader2 className="w-4 h-4 animate-spin" />
                                       : 'Valider'}
                                   </Button>
                                 )}
                               </>
                             ) : isRejected ? (
                               <Badge color="red">Rejeté</Badge>
                             ) : (
                               <CheckCircle className="w-5 h-5 text-green-500" />
                             )}
                          </div>
                       </div>
                     );
                  })}
               </div>
            ) : <p className="text-sm text-slate-500 italic">Aucun paiement enregistré.</p>}
         </div>
      </div>

      <CancelInvoiceModal
        isOpen={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        onConfirm={handleCancel}
        isLoading={actionLoading === 'cancel'}
      />
    </div>
  );
};

// --- Delivery Note Detail ---

interface DeliveryNoteDetailProps {
  deliveryNote: DeliveryNote;
  onClose: () => void;
  onRefresh: () => void;
  userRole?: string;
}

const DeliveryNoteDetail = ({ deliveryNote, onClose, onRefresh, userRole }: DeliveryNoteDetailProps) => {
  const isCommercial = userRole === 'COMMERCIAL';
  const canConfirmDelivery = userRole === 'ADMIN' || userRole === 'MAGASINIER';
  const [signatureClient, setSignatureClient] = useState('');
  const [signatureLivreur, setSignatureLivreur] = useState('');
  const [nomReceptionnaire, setNomReceptionnaire] = useState('');
  const [observations, setObservations] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  const { toast } = useToast();

  const handleConfirm = async () => {
    if (!nomReceptionnaire) {
      toast('Veuillez renseigner le nom du réceptionnaire.', 'warning');
      return;
    }
    setConfirmLoading(true);
    try {
      await salesService.confirmDelivery(deliveryNote.uuid, {
        signature_client: signatureClient || undefined,
        signature_livreur: signatureLivreur || undefined,
        nom_receptionnaire: nomReceptionnaire,
        observations_livraison: observations || undefined,
      });
      toast('Livraison confirmée avec succès', 'success');
      onRefresh();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la confirmation', 'error');
    } finally {
      setConfirmLoading(false);
    }
  };

  const handlePdf = async () => {
    try {
      const blob = await salesService.getDeliveryNotePdf(deliveryNote.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${deliveryNote.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.message || 'Erreur lors du téléchargement du PDF', 'error');
    }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full md:w-[800px] bg-white shadow-2xl z-40 flex flex-col border-l border-slate-200">
      <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-start bg-slate-50">
         <div>
            <div className="flex items-center gap-3">
               <h2 className="text-2xl font-bold text-slate-900">{deliveryNote.id}</h2>
               <DeliveryNoteStatusBadge status={deliveryNote.status} />
            </div>
            <p className="text-slate-500">{deliveryNote.clientName}</p>
         </div>
         <button onClick={onClose}><X className="w-6 h-6 text-slate-400 hover:text-slate-600" /></button>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
         {/* Info */}
         <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
               <span className="text-xs text-slate-500 uppercase">Date de livraison</span>
               <div className="text-sm font-medium text-slate-900">{new Date(deliveryNote.date).toLocaleDateString('fr-FR')}</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
               <span className="text-xs text-slate-500 uppercase">Facture</span>
               <div className="text-sm font-medium text-slate-900">{deliveryNote.invoiceCode}</div>
            </div>
            {deliveryNote.address && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                 <span className="text-xs text-slate-500 uppercase">Adresse</span>
                 <div className="text-sm font-medium text-slate-900">{deliveryNote.address}</div>
              </div>
            )}
            {deliveryNote.driverName && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                 <span className="text-xs text-slate-500 uppercase">Livreur</span>
                 <div className="text-sm font-medium text-slate-900">{deliveryNote.driverName}</div>
              </div>
            )}
         </div>

         {/* Actions */}
         <div className="flex gap-3">
            <Button variant="secondary" icon={Printer} onClick={handlePdf}>Télécharger PDF</Button>
         </div>

         {/* Lines */}
         <div>
            <h3 className="font-bold text-slate-900 mb-3">Lignes de livraison</h3>
            <table className="min-w-full divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
               <thead className="bg-slate-50">
                  <tr>
                     <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Produit</th>
                     <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté Commandée</th>
                     <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté Livrée</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                  {deliveryNote.items.map((item, i) => (
                     <tr key={i}>
                        <td className="px-4 py-2 text-sm">{item.productName}</td>
                        <td className="px-4 py-2 text-center text-sm">{item.quantityOrdered}</td>
                        <td className="px-4 py-2 text-center text-sm font-medium">{item.quantityDelivered}</td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </div>

         {/* Signature section - only for PENDING, not for commercial */}
         {deliveryNote.status === 'PENDING' && canConfirmDelivery && (
           <div className="space-y-4 border-t border-slate-200 pt-6">
             <h3 className="font-bold text-slate-900">Confirmer la Livraison</h3>
             <Input
               label="Nom du réceptionnaire"
               required
               value={nomReceptionnaire}
               onChange={e => setNomReceptionnaire(e.target.value)}
               placeholder="Nom complet du réceptionnaire..."
             />
             <div className="grid grid-cols-2 gap-4">
               <SignaturePad label="Signature Client (optionnel)" onSave={setSignatureClient} />
               <SignaturePad label="Signature Livreur (optionnel)" onSave={setSignatureLivreur} />
             </div>
             <FieldHint>Les signatures électroniques sont facultatives — les signatures physiques sur le BL imprimé suffisent.</FieldHint>
             <div>
               <label className="block text-sm font-medium text-slate-700 mb-1">Observations</label>
               <textarea
                 className="w-full border-slate-300 rounded-md p-2 text-sm"
                 rows={2}
                 value={observations}
                 onChange={e => setObservations(e.target.value)}
                 placeholder="Observations sur la livraison..."
               />
             </div>
             <Button
               onClick={handleConfirm}
               disabled={!nomReceptionnaire || confirmLoading}
               icon={CheckCircle}
             >
               {confirmLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Confirmation...</> : 'Confirmer la Livraison'}
             </Button>
           </div>
         )}

         {/* Show existing signatures if delivered */}
         {deliveryNote.status === 'DELIVERED' && (deliveryNote.signatureClient || deliveryNote.signatureLivreur) && (
           <div className="border-t border-slate-200 pt-6">
             <h3 className="font-bold text-slate-900 mb-3">Signatures</h3>
             <div className="grid grid-cols-2 gap-4">
               {deliveryNote.signatureClient && (
                 <div>
                   <span className="text-xs font-bold uppercase text-slate-500">Signature Client</span>
                   <img src={deliveryNote.signatureClient} alt="Signature client" className="border rounded mt-1 max-h-32" />
                 </div>
               )}
               {deliveryNote.signatureLivreur && (
                 <div>
                   <span className="text-xs font-bold uppercase text-slate-500">Signature Livreur</span>
                   <img src={deliveryNote.signatureLivreur} alt="Signature livreur" className="border rounded mt-1 max-h-32" />
                 </div>
               )}
             </div>
           </div>
         )}
      </div>
    </div>
  );
};

// --- Cautions & Dépôts ---

const CautionsDepots = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'contrat_code', dir: 'desc' });
  const toggleSort = (col: string) => setSort(s => s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/cautions`, { params: { page_size: 100 } });
      setData(res.data?.data || []);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalMontant = data.reduce((s: number, r: any) => s + Number(r.montant || 0), 0);

  const sortedData = [...data].sort((a: any, b: any) => {
    const va = a[sort.key] ?? '';
    const vb = b[sort.key] ?? '';
    const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const SortIcon = ({ col }: { col: string }) => {
    if (sort.key !== col) return <ArrowUpDown className="w-3 h-3 inline ml-1 text-slate-300" />;
    return sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 inline ml-1" /> : <ArrowDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <span className="text-sm text-slate-500">{data.length} caution{data.length > 1 ? 's' : ''} de location — Total: {formatCurrency(totalMontant)}</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('contrat_code')}>Contrat<SortIcon col="contrat_code" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('client_nom')}>Client<SortIcon col="client_nom" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('machine_nom')}>Machine<SortIcon col="machine_nom" /></th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('montant')}>Montant<SortIcon col="montant" /></th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('payee')}>Payé<SortIcon col="payee" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('mode_paiement')}>Mode<SortIcon col="mode_paiement" /></th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('reference_paiement')}>Référence<SortIcon col="reference_paiement" /></th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('restituee')}>Restitué<SortIcon col="restituee" /></th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('montant_retenu')}>Retenu<SortIcon col="montant_retenu" /></th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700" onClick={() => toggleSort('statut_contrat')}>Statut<SortIcon col="statut_contrat" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={10} className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && sortedData.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-500"><Shield className="w-10 h-10 mx-auto text-slate-300 mb-2" />Aucune caution trouvée</td></tr>}
            {!loading && sortedData.map((r: any, i: number) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{r.contrat_code}</td>
                <td className="px-4 py-3 text-slate-600">{r.client_nom}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.machine_nom}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(r.montant)}</td>
                <td className="px-4 py-3 text-center">{r.payee ? <span className="text-green-600 font-bold">Oui</span> : <span className="text-slate-400">Non</span>}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.mode_paiement ? (PAYMENT_LABELS[r.mode_paiement] || r.mode_paiement) : '—'}</td>
                <td className="px-4 py-3 text-slate-600 text-xs font-mono">{r.reference_paiement || '—'}</td>
                <td className="px-4 py-3 text-center">{r.restituee ? <span className="text-blue-600 font-bold">Oui</span> : <span className="text-slate-400">Non</span>}</td>
                <td className="px-4 py-3 text-right font-mono">{r.montant_retenu > 0 ? formatCurrency(r.montant_retenu) : '—'}</td>
                <td className="px-4 py-3 text-center text-xs text-slate-500">{r.statut_contrat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// --- Main Sales Module View ---

export const SalesModule = ({ canWrite, userRole }: { canWrite?: boolean; userRole?: string }) => {
  const isMagasinier = userRole === 'MAGASINIER';
  const isCommercialOrGerant = userRole === 'COMMERCIAL' || userRole === 'GERANT';
  const canWriteQuotes = canWrite ?? false;
  // COMMERCIAL and GERANT cannot create invoices — only ADMIN/COMPTABLE
  const canWriteInvoices = (canWrite ?? false) && !isCommercialOrGerant;
  const canWritePayments = canWrite ?? false;
  const canWriteDeliveries = canWrite ?? false;
  const isGerantOrAdmin = userRole === 'GERANT' || userRole === 'ADMIN';
  const canSeeCautions = ['ADMIN', 'GERANT', 'COMPTABLE'].includes(userRole || '');
  const { toast } = useToast();
  // MAGASINIER defaults to BL tab (only tab they can see)
  const [view, setView] = useState<'quotes' | 'invoices' | 'bl' | 'cautions'>(isMagasinier ? 'bl' : 'quotes');
  const [pendingContractsCount, setPendingContractsCount] = useState(0);
  const [isQuoteWizardOpen, setIsQuoteWizardOpen] = useState(false);
  const [isInvoiceWizardOpen, setIsInvoiceWizardOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState<DeliveryNote | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isDeliveryNoteModalOpen, setIsDeliveryNoteModalOpen] = useState(false);

  // Quote action modals
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [cancelQuoteConfirmOpen, setCancelQuoteConfirmOpen] = useState(false);
  const [quoteActionLoading, setQuoteActionLoading] = useState('');

  // Filters
  const [filter, setFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [saleTypeFilter, setSaleTypeFilter] = useState<'' | 'VENTE' | 'LOCATION' | 'LOA'>('');

  // Debounce text filter (400ms)
  useEffect(() => {
    const timer = setTimeout(() => { setDebouncedFilter(filter); setInvoicePage(1); setQuotePage(1); setBlPage(1); }, 400);
    return () => clearTimeout(timer);
  }, [filter]);

  // API state
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const PAGE_SIZE = 20;

  // Invoice pagination
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoicePagination, setInvoicePagination] = useState({ totalPages: 1, totalItems: 0 });
  const INVOICE_PAGE_SIZE = PAGE_SIZE;

  // Invoice sorting
  type InvoiceSortKey = 'id' | 'typeVente' | 'clientName' | 'type' | 'total' | 'remainingAmount' | 'status' | 'date';
  const [invoiceSort, setInvoiceSort] = useState<{ key: InvoiceSortKey; dir: 'asc' | 'desc' }>({ key: 'status', dir: 'asc' });

  // Map frontend sort keys to backend column names
  const sortKeyMap: Record<InvoiceSortKey, string> = {
    id: 'code', typeVente: 'type_vente', clientName: 'client_nom',
    type: 'type_facture', total: 'montant_ttc', remainingAmount: 'montant_restant',
    status: 'status', date: 'date_facture',
  };

  const toggleInvoiceSort = (key: InvoiceSortKey) => {
    setInvoiceSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setInvoicePage(1);
  };

  const SortIcon = ({ col }: { col: InvoiceSortKey }) => {
    if (invoiceSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return invoiceSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // Quote pagination & sorting
  const [quotePage, setQuotePage] = useState(1);
  const [quotePagination, setQuotePagination] = useState({ totalPages: 1, totalItems: 0 });

  type QuoteSortKey = 'id' | 'typeVente' | 'clientName' | 'date' | 'total' | 'status';
  const [quoteSort, setQuoteSort] = useState<{ key: QuoteSortKey; dir: 'asc' | 'desc' }>({ key: 'status', dir: 'asc' });

  const quoteSortKeyMap: Record<QuoteSortKey, string> = {
    id: 'code', typeVente: 'type_vente', clientName: 'client_nom',
    date: 'date_validite', total: 'montant_ttc', status: 'status',
  };

  const toggleQuoteSort = (key: QuoteSortKey) => {
    setQuoteSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setQuotePage(1);
  };

  const QuoteSortIcon = ({ col }: { col: QuoteSortKey }) => {
    if (quoteSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return quoteSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // BL (Delivery Notes) pagination & sorting
  const [blPage, setBlPage] = useState(1);
  const [blPagination, setBlPagination] = useState({ totalPages: 1, totalItems: 0 });

  type BlSortKey = 'id' | 'typeVente' | 'clientName' | 'invoiceCode' | 'date' | 'status';
  const [blSort, setBlSort] = useState<{ key: BlSortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });

  const blSortKeyMap: Record<BlSortKey, string> = {
    id: 'code', typeVente: 'type_vente', clientName: 'client_nom',
    invoiceCode: 'facture_code', date: 'date_livraison', status: 'status',
  };

  const toggleBlSort = (key: BlSortKey) => {
    setBlSort(prev => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
    setBlPage(1);
  };

  const BlSortIcon = ({ col }: { col: BlSortKey }) => {
    if (blSort.key !== col) return <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />;
    return blSort.dir === 'asc'
      ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />;
  };

  // Fetch invoices (server-side: filter, search, sort, pagination)
  const fetchInvoices = useCallback(async () => {
    try {
      const params: any = {
        page: invoicePage,
        page_size: INVOICE_PAGE_SIZE,
        sort_by: sortKeyMap[invoiceSort.key],
        sort_dir: invoiceSort.dir,
      };
      if (saleTypeFilter) params.type_vente = saleTypeFilter;
      if (debouncedFilter.trim()) params.search = debouncedFilter.trim();
      const res = await salesService.listInvoices(params);
      setInvoices(res.data.map(transformInvoice));
      setInvoicePagination({
        totalPages: res.pagination.total_pages,
        totalItems: res.pagination.total_items,
      });
    } catch { /* handled by main fetchData error */ }
  }, [saleTypeFilter, invoicePage, debouncedFilter, invoiceSort]);

  // Fetch quotes (server-side: filter, search, sort, pagination)
  const fetchQuotes = useCallback(async () => {
    try {
      const params: any = {
        page: quotePage,
        page_size: PAGE_SIZE,
        sort_by: quoteSortKeyMap[quoteSort.key],
        sort_dir: quoteSort.dir,
      };
      if (saleTypeFilter) params.type_vente = saleTypeFilter;
      if (debouncedFilter.trim()) params.search = debouncedFilter.trim();
      const res = await salesService.listQuotes(params);
      setQuotes(res.data.map(transformQuote));
      setQuotePagination({
        totalPages: res.pagination.total_pages,
        totalItems: res.pagination.total_items,
      });
    } catch { /* handled by main fetchData error */ }
  }, [saleTypeFilter, quotePage, debouncedFilter, quoteSort]);

  // Fetch delivery notes (server-side: filter, search, sort, pagination)
  const fetchDeliveryNotes = useCallback(async () => {
    try {
      const params: any = {
        page: blPage,
        page_size: PAGE_SIZE,
        sort_by: blSortKeyMap[blSort.key],
        sort_dir: blSort.dir,
      };
      if (saleTypeFilter) params.type_vente = saleTypeFilter;
      if (debouncedFilter.trim()) params.search = debouncedFilter.trim();
      const res = await salesService.listDeliveryNotes(params);
      setDeliveryNotes(res.data.map(transformDeliveryNote));
      setBlPagination({
        totalPages: res.pagination.total_pages,
        totalItems: res.pagination.total_items,
      });
    } catch { /* handled by main fetchData error */ }
  }, [saleTypeFilter, blPage, debouncedFilter, blSort]);

  // Fetch all other data (clients, products, etc.)
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch clients — MAGASINIER has CLIENTS:NONE so this may 403; catch individually
      try {
        const clientsRes = await clientsService.list({ page_size: 100, is_active: true });
        setClients(clientsRes.data.map(transformClient));
      } catch {
        setClients([]);
      }

      // Fetch products for creation
      try {
        const stockRes = await stockService.listStock({ page_size: 100 });
        setProducts(stockRes.data.map(p => ({
          id: p.id,
          name: p.designation,
          priceSale: p.prix_vente_public,
          priceWholesale: p.prix_vente_grossiste,
        })));
      } catch {
        setProducts([]);
      }

      // Fetch pending contracts count for GÉRANT/ADMIN
      if (isGerantOrAdmin) {
        try {
          const [rentalPending, loaPending] = await Promise.all([
            rentalService.listPendingApproval(),
            loaService.listPendingApproval(),
          ]);
          setPendingContractsCount((rentalPending.total || 0) + (loaPending.total || 0));
        } catch {
          setPendingContractsCount(0);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  }, [isGerantOrAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch invoices on filter/page/sort change
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Fetch quotes on filter/page/sort change
  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  // Fetch delivery notes on filter/page/sort change
  useEffect(() => {
    fetchDeliveryNotes();
  }, [fetchDeliveryNotes]);

  // Reset to page 1 when filter changes
  const handleSaleTypeFilterChange = (type: '' | 'VENTE' | 'LOCATION' | 'LOA') => {
    setSaleTypeFilter(type);
    setInvoicePage(1);
    setQuotePage(1);
    setBlPage(1);
  };

  // Refresh all data including invoices, quotes, and delivery notes
  const refreshAll = useCallback(() => {
    fetchData();
    fetchInvoices();
    fetchQuotes();
    fetchDeliveryNotes();
  }, [fetchData, fetchInvoices, fetchQuotes, fetchDeliveryNotes]);

  const handleQuoteSaved = () => {
    toast('Devis créé avec succès', 'success');
    refreshAll();
  };

  // IMPORTANT: All callbacks below use refreshAll() to reload both general data and invoices

  const handleViewQuote = async (q: Quote) => {
    try {
      const fullQuote = await salesService.getQuote(q.uuid);
      setSelectedQuote(transformQuote(fullQuote));
    } catch {
      setSelectedQuote(q);
    }
  };

  const handleViewInvoice = async (inv: Invoice) => {
    try {
      // The detail endpoint already embeds `paiements` (with status + reference),
      // so no extra /payments round-trip is needed.
      const fullInvoice = await salesService.getInvoice(inv.uuid);
      setSelectedInvoice(transformInvoice(fullInvoice));
    } catch {
      setSelectedInvoice(inv);
    }
  };

  const handlePrintQuote = async (q: Quote) => {
    try {
      const blob = await salesService.getQuotePdf(q.uuid);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${q.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast(err.message || 'Erreur lors du téléchargement du PDF', 'error');
    }
  };

  // Quote actions
  const handleSendQuote = async (q: Quote) => {
    setQuoteActionLoading('send');
    try {
      await salesService.sendQuote(q.uuid);
      toast('Devis envoyé avec succès', 'success');
      refreshAll();
      if (selectedQuote?.uuid === q.uuid) {
        const updated = await salesService.getQuote(q.uuid);
        setSelectedQuote(transformQuote(updated));
      }
    } catch (err: any) {
      toast(err.message || "Erreur lors de l'envoi", 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleAcceptQuote = async (quote?: Quote) => {
    const target = quote || selectedQuote;
    if (!target) return;
    setQuoteActionLoading('accept');
    try {
      await salesService.acceptQuote(target.uuid);
      toast('Devis accepté', 'success');
      refreshAll();
      if (selectedQuote?.uuid === target.uuid) {
        const updated = await salesService.getQuote(target.uuid);
        setSelectedQuote(transformQuote(updated));
      }
    } catch (err: any) {
      toast(err.message || "Erreur lors de l'acceptation", 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleRejectQuote = async (reason: string) => {
    if (!selectedQuote) return;
    setQuoteActionLoading('reject');
    try {
      await salesService.rejectQuote(selectedQuote.uuid, reason);
      const updated = await salesService.getQuote(selectedQuote.uuid);
      setSelectedQuote(transformQuote(updated));
      setRejectModalOpen(false);
      refreshAll();
    } catch (err: any) {
      toast(err.message || 'Erreur lors du rejet', 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleConvertQuote = async (quote?: Quote) => {
    const target = quote || selectedQuote;
    if (!target) return;
    setQuoteActionLoading('convert');
    try {
      await salesService.convertQuoteToInvoice(target.uuid);
      toast('Devis converti en facture', 'success');
      setSelectedQuote(null);
      await refreshAll();
      setView('invoices');
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la conversion', 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleCancelQuote = async () => {
    if (!selectedQuote) return;
    setQuoteActionLoading('cancel');
    try {
      await salesService.cancelQuote(selectedQuote.uuid);
      const updated = await salesService.getQuote(selectedQuote.uuid);
      setSelectedQuote(transformQuote(updated));
      setCancelQuoteConfirmOpen(false);
      refreshAll();
    } catch (err: any) {
      toast(err.message || "Erreur lors de l'annulation", 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleDuplicateQuote = async () => {
    if (!selectedQuote) return;
    setQuoteActionLoading('duplicate');
    try {
      // Get full quote data and create a new one with same lines
      const fullQuote = await salesService.getQuote(selectedQuote.uuid);
      const newValidite = new Date();
      newValidite.setDate(newValidite.getDate() + 30);
      await salesService.createQuote({
        client_id: fullQuote.client_id,
        site_stock_id: fullQuote.site_stock_id,
        type_vente: fullQuote.type_vente,
        date_validite: newValidite.toISOString().split('T')[0],
        lignes: fullQuote.lignes.map(l => ({
          product_id: l.product_id,
          designation: l.designation,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire,
          taux_remise: l.taux_remise,
        })),
        notes: fullQuote.notes,
      });
      setSelectedQuote(null);
      refreshAll();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la duplication', 'error');
    } finally {
      setQuoteActionLoading('');
    }
  };

  const handleAddPayment = async (paymentData: PaymentCreate) => {
    try {
      // The backend validates the payment in the same transaction when the user
      // is allowed to (ADMIN/COMPTABLE) — no second HTTP call, so a payment can
      // no longer end up stuck in PENDING because that call failed.
      const payment = await salesService.createPayment(paymentData);
      if (payment.status === 'VALIDATED') {
        toast('Paiement enregistré et validé', 'success');
      } else {
        toast(
          'Paiement enregistré, en attente de validation par un comptable. '
          + 'Ne le ressaisissez pas : il bloque déjà le solde de la facture.',
          'warning'
        );
      }
      setIsPaymentModalOpen(false);
      if (selectedInvoice) {
        handleViewInvoice(selectedInvoice);
      }
      refreshAll();
    } catch (err: any) {
      toast(err.message || "Erreur lors de l'enregistrement du paiement", 'error');
    }
  };

  // All three tables are now server-side filtered/sorted/paginated
  const filteredQuotes = quotes;
  const filteredInvoices = invoices;
  const filteredDeliveryNotes = deliveryNotes;

  return (
    <div className="space-y-6">
      {/* Module Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
           <h2 className="text-2xl font-bold text-slate-800">Ventes & Facturation</h2>
           <div className="flex gap-4 mt-2 border-b border-transparent">
              {!isMagasinier && <button onClick={() => setView('quotes')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'quotes' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Devis</button>}
              {!isMagasinier && <button onClick={() => setView('invoices')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'invoices' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Factures</button>}
              <button onClick={() => setView('bl')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'bl' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Bons de Livraison</button>
              {canSeeCautions && <button onClick={() => setView('cautions')} className={`pb-2 text-sm font-medium border-b-2 transition-colors ${view === 'cautions' ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>Cautions & Dépôts</button>}
           </div>
        </div>
        <div className="flex gap-2">
          {view === 'quotes' && canWriteQuotes && <Button icon={Plus} onClick={() => setIsQuoteWizardOpen(true)}>Nouveau Devis</Button>}
          {view === 'invoices' && canWriteInvoices && <Button icon={Plus} onClick={() => setIsInvoiceWizardOpen(true)}>Nouvelle Facture</Button>}
        </div>
      </div>

      {/* Pending contracts banner for GÉRANT/ADMIN */}
      {isGerantOrAdmin && pendingContractsCount > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">
            {pendingContractsCount} contrat{pendingContractsCount > 1 ? 's' : ''} en attente d'approbation
          </span>
        </div>
      )}

      {/* Main Content Area */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm min-h-[500px]">
         {/* Shared Toolbar */}
         <div className="p-4 border-b border-slate-200 flex gap-4 bg-slate-50">
            <div className="relative flex-1 max-w-md">
               <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
               <input
                  className="pl-9 w-full border-slate-300 rounded-md text-sm py-2"
                  placeholder="Rechercher numéro, client..."
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
               />
            </div>
            <Button variant="secondary" icon={RefreshCw} onClick={refreshAll} disabled={isLoading}>Actualiser</Button>
         </div>

         {/* Views */}
         {view === 'quotes' && (
            <>
            <table className="min-w-full divide-y divide-slate-200">
               <thead className="bg-slate-50">
                  <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('id')}>Numéro<QuoteSortIcon col="id" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('typeVente')}>Type<QuoteSortIcon col="typeVente" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('clientName')}>Client<QuoteSortIcon col="clientName" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('date')}>Date<QuoteSortIcon col="date" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('total')}>Montant TTC<QuoteSortIcon col="total" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleQuoteSort('status')}>Statut<QuoteSortIcon col="status" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                  {isLoading && (
                     <tr>
                        <td colSpan={7} className="px-6 py-12 text-center">
                           <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                           <p className="text-slate-500 mt-2">Chargement...</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && error && (
                     <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-red-500">
                           <AlertTriangle className="w-8 h-8 mx-auto text-red-400 mb-2" />
                           <p>{error}</p>
                           <Button variant="ghost" onClick={refreshAll} className="mt-2">Réessayer</Button>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredQuotes.length === 0 && (
                     <tr>
                        <td colSpan={7} className="px-6 py-12 text-center text-slate-500">
                           <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                           <p>Aucun devis trouvé</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredQuotes.map(q => (
                     <tr key={q.uuid} className="hover:bg-slate-50">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{q.id}</td>
                        <td className="px-6 py-4 text-center"><SaleTypeBadge type={q.typeVente} /></td>
                        <td className="px-6 py-4 text-sm text-slate-600">{q.clientName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{new Date(q.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-6 py-4 text-sm text-right font-bold text-slate-900">{formatCurrency(q.total)}</td>
                        <td className="px-6 py-4 text-center"><QuoteStatusBadge status={q.status} /></td>
                        <td className="px-6 py-4 text-right">
                           <div className="flex justify-end gap-2">
                              <button className="text-slate-400 hover:text-blue-600" title="Voir le devis" onClick={() => handleViewQuote(q)}><Eye className="w-4 h-4" /></button>
                              <button className="text-slate-400 hover:text-slate-600" title="Télécharger PDF" onClick={() => handlePrintQuote(q)}><Printer className="w-4 h-4" /></button>
                              {q.status === QuoteStatus.DRAFT && (
                                <button className="text-slate-400 hover:text-blue-600" title="Envoyer au client" onClick={() => handleSendQuote(q)}><Send className="w-4 h-4" /></button>
                              )}
                              {q.status === QuoteStatus.SENT && (
                                <button className="text-slate-400 hover:text-green-600" title="Accepter le devis" onClick={() => handleAcceptQuote(q)}><CheckCircle className="w-4 h-4" /></button>
                              )}
                              {(q.status === QuoteStatus.ACCEPTED) && (
                                <button className="text-slate-400 hover:text-purple-600" title={isCommercialOrGerant ? "Envoyer en facturation" : "Convertir en facture"} onClick={() => handleConvertQuote(q)}><ArrowRightLeft className="w-4 h-4" /></button>
                              )}
                           </div>
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
            {/* Quote Pagination */}
            {quotePagination.totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  {quotePagination.totalItems} devis
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={quotePage <= 1}
                    onClick={() => setQuotePage(p => p - 1)}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {quotePage} sur {quotePagination.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={quotePage >= quotePagination.totalPages}
                    onClick={() => setQuotePage(p => p + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
            </>
         )}

         {view === 'invoices' && (
            <>
            <div className="px-4 py-2 flex gap-2 border-b border-slate-100">
              {(['', 'VENTE', 'LOCATION', 'LOA'] as const).map(type => (
                <button
                  key={type}
                  onClick={() => handleSaleTypeFilterChange(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    saleTypeFilter === type
                      ? 'bg-primary-100 text-primary-700 border border-primary-300'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-transparent'
                  }`}
                >
                  {type === '' ? 'Toutes' : type === 'VENTE' ? 'Vente' : type === 'LOCATION' ? 'Location' : 'LOA'}
                </button>
              ))}
            </div>
            <table className="min-w-full divide-y divide-slate-200">
               <thead className="bg-slate-50">
                  <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('id')}>Numéro<SortIcon col="id" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('typeVente')}>Type Vente<SortIcon col="typeVente" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('clientName')}>Client<SortIcon col="clientName" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('type')}>Type<SortIcon col="type" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('total')}>Total<SortIcon col="total" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('remainingAmount')}>Reste Dû<SortIcon col="remainingAmount" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleInvoiceSort('status')}>Statut<SortIcon col="status" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                  {isLoading && (
                     <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                           <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                           <p className="text-slate-500 mt-2">Chargement...</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredInvoices.length === 0 && (
                     <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                           <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                           <p>Aucune facture trouvée</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredInvoices.map(inv => (
                     <tr key={inv.uuid} className="hover:bg-slate-50 cursor-pointer" onClick={() => handleViewInvoice(inv)}>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{inv.id}</td>
                        <td className="px-6 py-4 text-center"><SaleTypeBadge type={inv.typeVente} /></td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-600">{inv.clientName}</div>
                          {inv.dateDebutLocation && (
                            <div className="text-[10px] text-blue-600 mt-0.5">
                              {new Date(inv.dateDebutLocation).toLocaleDateString('fr-FR')} → {inv.dateFinLocation ? new Date(inv.dateFinLocation).toLocaleDateString('fr-FR') : '...'}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{inv.type}</td>
                        <td className="px-6 py-4 text-sm text-right font-medium">{formatCurrency(inv.total)}</td>
                        <td className="px-6 py-4 text-sm text-right font-bold text-red-600">{formatCurrency(inv.remainingAmount)}</td>
                        <td className="px-6 py-4 text-center"><InvoiceStatusBadge status={inv.status} /></td>
                        <td className="px-6 py-4 text-right">
                           <ChevronRight className="w-5 h-5 text-slate-400 inline" />
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
            {/* Invoice Pagination */}
            {invoicePagination.totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  {invoicePagination.totalItems} facture{invoicePagination.totalItems > 1 ? 's' : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={invoicePage <= 1}
                    onClick={() => setInvoicePage(p => p - 1)}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {invoicePage} sur {invoicePagination.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={invoicePage >= invoicePagination.totalPages}
                    onClick={() => setInvoicePage(p => p + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
            </>
         )}

         {view === 'bl' && (
            <>
              {/* Pending BL notification */}
              {(() => {
                const pendingCount = deliveryNotes.filter(dn => dn.status === 'PENDING').length;
                return pendingCount > 0 ? (
                  <div className="mx-4 mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2 text-yellow-800 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium">{pendingCount} bon(s) de livraison en attente de validation</span>
                  </div>
                ) : null;
              })()}
            <table className="min-w-full divide-y divide-slate-200">
               <thead className="bg-slate-50">
                  <tr>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('id')}>Numéro<BlSortIcon col="id" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('typeVente')}>Type<BlSortIcon col="typeVente" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('clientName')}>Client<BlSortIcon col="clientName" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('invoiceCode')}>Facture<BlSortIcon col="invoiceCode" /></th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase">Produits livrés</th>
                     <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('date')}>Date<BlSortIcon col="date" /></th>
                     <th className="px-6 py-3 text-center text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none" onClick={() => toggleBlSort('status')}>Statut<BlSortIcon col="status" /></th>
                     <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-slate-200">
                  {isLoading && (
                     <tr>
                        <td colSpan={8} className="px-6 py-12 text-center">
                           <Loader2 className="w-8 h-8 mx-auto text-slate-400 animate-spin" />
                           <p className="text-slate-500 mt-2">Chargement...</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredDeliveryNotes.length === 0 && (
                     <tr>
                        <td colSpan={8} className="px-6 py-12 text-center text-slate-500">
                           <Truck className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                           <p>Aucun bon de livraison trouvé</p>
                        </td>
                     </tr>
                  )}
                  {!isLoading && !error && filteredDeliveryNotes.map(dn => (
                     <tr key={dn.uuid} className="hover:bg-slate-50 cursor-pointer" onClick={async () => {
                       try {
                         const detail = await salesService.getDeliveryNote(dn.uuid);
                         setSelectedDeliveryNote(transformDeliveryNote(detail));
                       } catch {
                         setSelectedDeliveryNote(dn);
                       }
                     }}>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{dn.id}</td>
                        <td className="px-6 py-4 text-center"><SaleTypeBadge type={dn.typeVente} /></td>
                        <td className="px-6 py-4 text-sm text-slate-600">{dn.clientName}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{dn.invoiceCode}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                           {dn.items.length > 0 ? (
                             <div className="space-y-0.5">
                               {dn.items.slice(0, 3).map((item, i) => (
                                 <div key={i} className="text-xs">
                                   <span className="font-medium">{item.productName}</span>
                                   <span className="text-slate-400 ml-1">×{item.quantityDelivered}/{item.quantityOrdered}</span>
                                 </div>
                               ))}
                               {dn.items.length > 3 && <div className="text-xs text-slate-400">+{dn.items.length - 3} autre(s)</div>}
                             </div>
                           ) : (
                             <span className="text-slate-400 text-xs">-</span>
                           )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{new Date(dn.date).toLocaleDateString('fr-FR')}</td>
                        <td className="px-6 py-4 text-center"><DeliveryNoteStatusBadge status={dn.status} /></td>
                        <td className="px-6 py-4 text-right">
                           <ChevronRight className="w-5 h-5 text-slate-400 inline" />
                        </td>
                     </tr>
                  ))}
               </tbody>
            </table>
            {/* BL Pagination */}
            {blPagination.totalPages > 1 && (
              <div className="px-4 py-3 flex items-center justify-between border-t border-slate-200">
                <div className="text-sm text-slate-500">
                  {blPagination.totalItems} bon{blPagination.totalItems > 1 ? 's' : ''} de livraison
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={blPage <= 1}
                    onClick={() => setBlPage(p => p - 1)}
                  >
                    Précédent
                  </Button>
                  <span className="text-sm text-slate-600">
                    Page {blPage} sur {blPagination.totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={blPage >= blPagination.totalPages}
                    onClick={() => setBlPage(p => p + 1)}
                  >
                    Suivant
                  </Button>
                </div>
              </div>
            )}
            </>
         )}

         {/* Cautions & Dépôts */}
         {view === 'cautions' && <CautionsDepots />}
      </div>

      {/* Modals & Drawers */}
      {isQuoteWizardOpen && (
        <CreateQuoteWizard
          onClose={() => setIsQuoteWizardOpen(false)}
          onSave={handleQuoteSaved}
          clients={clients}
          products={products}
          userRole={userRole}
        />
      )}

      {isInvoiceWizardOpen && (
        <CreateInvoiceWizard
          onClose={() => setIsInvoiceWizardOpen(false)}
          onSave={() => { toast('Facture créée avec succès', 'success'); refreshAll(); }}
          clients={clients}
          products={products}
          userRole={userRole}
        />
      )}

      {selectedQuote && (
        <>
          <div className="fixed inset-0 bg-slate-900/50 z-30" onClick={() => setSelectedQuote(null)} />
          <div className="fixed inset-y-0 right-0 w-full md:w-[700px] bg-white shadow-2xl z-40 flex flex-col border-l border-slate-200">
            <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-start bg-slate-50">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-2xl font-bold text-slate-900">{selectedQuote.id}</h2>
                  <QuoteStatusBadge status={selectedQuote.status} />
                </div>
                <p className="text-slate-500">{selectedQuote.clientName}</p>
              </div>
              <button onClick={() => setSelectedQuote(null)}><X className="w-6 h-6 text-slate-400 hover:text-slate-600" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-500 uppercase">Date</span>
                  <div className="text-sm font-medium text-slate-900">{new Date(selectedQuote.date).toLocaleDateString('fr-FR')}</div>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <span className="text-xs text-slate-500 uppercase">Valide jusqu'au</span>
                  <div className="text-sm font-medium text-slate-900">{new Date(selectedQuote.validUntil).toLocaleDateString('fr-FR')}</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <span className="text-xs text-blue-700 uppercase">Total TTC</span>
                  <div className="text-xl font-bold text-blue-900">{formatCurrency(selectedQuote.total)}</div>
                </div>
              </div>

              {/* Quote Actions */}
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" icon={Download} onClick={() => handlePrintQuote(selectedQuote)}>Télécharger PDF</Button>

                {selectedQuote.status === QuoteStatus.DRAFT && (
                  <Button icon={Send} onClick={() => handleSendQuote(selectedQuote)} disabled={quoteActionLoading === 'send'}>
                    {quoteActionLoading === 'send' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Envoyer au Client
                  </Button>
                )}

                {selectedQuote.status === QuoteStatus.SENT && (
                  <Button icon={CheckCircle} onClick={() => handleAcceptQuote()} disabled={quoteActionLoading === 'accept'}>
                    {quoteActionLoading === 'accept' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Accepter
                  </Button>
                )}

                {(selectedQuote.status === QuoteStatus.DRAFT || selectedQuote.status === QuoteStatus.SENT) && (
                  <Button variant="danger" icon={XCircle} onClick={() => setRejectModalOpen(true)}>Rejeter</Button>
                )}

                {selectedQuote.status === QuoteStatus.ACCEPTED && (
                  <Button icon={ArrowRightLeft} onClick={() => handleConvertQuote()} disabled={quoteActionLoading === 'convert'}>
                    {quoteActionLoading === 'convert' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {isCommercialOrGerant ? 'Envoyer en facturation' : 'Convertir en Facture'}
                  </Button>
                )}

                {selectedQuote.status !== QuoteStatus.CONVERTED && selectedQuote.status !== QuoteStatus.CANCELLED && (
                  <Button variant="ghost" icon={XCircle} onClick={() => setCancelQuoteConfirmOpen(true)}>Annuler</Button>
                )}

                <Button variant="ghost" icon={Copy} onClick={handleDuplicateQuote} disabled={quoteActionLoading === 'duplicate'}>Dupliquer</Button>
              </div>

              {selectedQuote.items.length > 0 && (
                <div>
                  <h3 className="font-bold text-slate-900 mb-3">Détail des lignes</h3>
                  <table className="min-w-full divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-slate-500">Produit</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-500">P.U.</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Qté</th>
                        <th className="px-4 py-2 text-center text-xs font-medium text-slate-500">Remise</th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-slate-500">Total HT</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {selectedQuote.items.map((item, i) => (
                        <tr key={i}>
                          <td className="px-4 py-2 text-sm text-slate-900">{item.productName}</td>
                          <td className="px-4 py-2 text-sm text-right text-slate-600">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-4 py-2 text-sm text-center text-slate-600">{item.quantity}</td>
                          <td className="px-4 py-2 text-sm text-center text-slate-600">{item.discount > 0 ? `-${item.discount}%` : '-'}</td>
                          <td className="px-4 py-2 text-sm text-right font-medium">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 text-right space-y-1">
                    <div className="text-sm text-slate-600">Sous-total HT : <span className="font-medium">{formatCurrency(selectedQuote.subtotal)}</span></div>
                    <div className="text-sm text-slate-600">TVA (18%) : <span className="font-medium">{formatCurrency(selectedQuote.tax)}</span></div>
                    <div className="text-base font-bold text-slate-900">Total TTC : {formatCurrency(selectedQuote.total)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <RejectQuoteModal
            isOpen={rejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
            onConfirm={handleRejectQuote}
            isLoading={quoteActionLoading === 'reject'}
          />

          <ConfirmationDialog
            isOpen={cancelQuoteConfirmOpen}
            onClose={() => setCancelQuoteConfirmOpen(false)}
            onConfirm={handleCancelQuote}
            title="Annuler le Devis"
            message={`Voulez-vous vraiment annuler le devis ${selectedQuote.id} ? Cette action est irréversible.`}
            confirmLabel="Annuler le Devis"
            variant="danger"
          />
        </>
      )}

      {selectedInvoice && (
         <>
           <div className="fixed inset-0 bg-slate-900/50 z-30" onClick={() => setSelectedInvoice(null)} />
           <InvoiceDetail
             invoice={selectedInvoice}
             onClose={() => setSelectedInvoice(null)}
             onAddPayment={() => setIsPaymentModalOpen(true)}
             onRefresh={() => {
               handleViewInvoice(selectedInvoice);
               refreshAll();
             }}
             onCreateDeliveryNote={() => setIsDeliveryNoteModalOpen(true)}
             userRole={userRole}
           />
           <AddPaymentModal
             isOpen={isPaymentModalOpen}
             onClose={() => setIsPaymentModalOpen(false)}
             invoice={selectedInvoice}
             onAddPayment={handleAddPayment}
           />
           {isDeliveryNoteModalOpen && (
             <CreateDeliveryNoteModal
               invoice={selectedInvoice}
               onClose={() => setIsDeliveryNoteModalOpen(false)}
               onSave={() => {
                 refreshAll();
                 setIsDeliveryNoteModalOpen(false);
               }}
             />
           )}
         </>
      )}

      {selectedDeliveryNote && (
        <>
          <div className="fixed inset-0 bg-slate-900/50 z-30" onClick={() => setSelectedDeliveryNote(null)} />
          <DeliveryNoteDetail
            deliveryNote={selectedDeliveryNote}
            onClose={() => setSelectedDeliveryNote(null)}
            onRefresh={() => {
              setSelectedDeliveryNote(null);
              refreshAll();
            }}
            userRole={userRole}
          />
        </>
      )}
    </div>
  );
};
